import {useMemo, useState} from 'react';
import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from 'react-query';
import {apiCall, note} from './useApi';

const getCustomerOrder = async () => await apiCall('get', '/customer/order');

export const useOrdersData = enabled => {
    note('useOrdersData called');
    const queryClient = useQueryClient();
    const {data} = useQuery('orders', getCustomerOrder, {placeholderData: [], enabled});
    const updateMutation = useMutation(payload => apiCall('put', '/customer/order', payload), {
        onSuccess: updated => queryClient.setQueryData('orders', updated),
    });
    const deleteMutation = useMutation(
        order_id => apiCall('delete', '/customer/order/' + order_id),
        {
            onSuccess: () => queryClient.invalidateQueries('orders'),
        }
    );
    return {
        ordersData: data,
        updateOrder: updateMutation.mutate,
        deleteOrder: deleteMutation.mutate,
    };
};

export const usePaginatedOrdersData = (enabled, pageSize = 100) => {
    const queryClient = useQueryClient();
    const [isLoadingAll, setIsLoadingAll] = useState(false);
    const {data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage} = useInfiniteQuery(
        ['orders', 'paginated', pageSize],
        ({pageParam = 1}) =>
            apiCall('get', '/customer/order/list', null, {
                params: {page: pageParam, page_size: pageSize},
            }),
        {
            enabled,
            getNextPageParam: lastPage => {
                const pagination = lastPage?.pagination;
                if (!pagination) return undefined;
                return pagination.current_page < pagination.total_pages
                    ? pagination.current_page + 1
                    : undefined;
            },
        }
    );
    const updateMutation = useMutation(payload => apiCall('put', '/customer/order', payload), {
        onSuccess: updated => {
            queryClient.setQueryData('orders', updated);
            queryClient.invalidateQueries(['orders', 'paginated']);
        },
    });
    const deleteMutation = useMutation(
        order_id => apiCall('delete', '/customer/order/' + order_id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('orders');
                queryClient.invalidateQueries(['orders', 'paginated']);
            },
        }
    );
    const ordersData = useMemo(() => data?.pages?.flatMap(page => page?.orders || []) || [], [data]);
    const totalCount = data?.pages?.[0]?.pagination?.total_items ?? 0;
    const loadedCount = ordersData.length;
    const hasMore = totalCount > 0 ? loadedCount < totalCount : !!hasNextPage;
    const loadAll = async () => {
        if (isLoadingAll || isFetchingNextPage || !hasMore) return;
        setIsLoadingAll(true);
        try {
            let firstPage = data?.pages?.[0];
            if (!firstPage) {
                firstPage = await apiCall('get', '/customer/order/list', null, {
                    params: {page: 1, page_size: pageSize},
                });
            }
            const totalPages = firstPage?.pagination?.total_pages || 1;
            const pageMap = new Map();
            (data?.pages || []).forEach(page => {
                if (page?.pagination?.current_page) pageMap.set(page.pagination.current_page, page);
            });
            if (!pageMap.has(1) && firstPage) pageMap.set(1, firstPage);

            for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
                if (!pageMap.has(pageNumber)) {
                    const pageData = await apiCall('get', '/customer/order/list', null, {
                        params: {page: pageNumber, page_size: pageSize},
                    });
                    pageMap.set(pageNumber, pageData);
                }
            }

            const pages = Array.from({length: totalPages}, (_, idx) => pageMap.get(idx + 1)).filter(Boolean);
            queryClient.setQueryData(['orders', 'paginated', pageSize], {
                pages,
                pageParams: pages.map(page => page?.pagination?.current_page),
            });
        } finally {
            setIsLoadingAll(false);
        }
    };
    return {
        ordersData,
        totalCount,
        loadedCount,
        hasMore,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        loadAll,
        isLoadingAll,
        updateOrder: updateMutation.mutate,
        deleteOrder: deleteMutation.mutate,
    };
};

/* see https://tkdodo.eu/blog/react-query-data-transformations for passing select function into
   hook as an argument, specifically avoiding excess rerenders. we were getting stuck in a loop. */
export const useAnswerSheetsData = () => {
    note('useAnswerSheetsData called');
    return useAnswerSheetsApi(selectAnswerSheetData);
};
const useAnswerSheetsApi = select => {
    const {data} = useQuery('orders', getCustomerOrder, {select});
    return {ordersData: data};
};
const selectAnswerSheetData = response => {
    return {
        entities: Array.from(new Set(response.map(item => item.registrant))).concat(''),
        schools: Array.from(new Set(response.map(item => item.school_name))).concat(''),
        exams: Array.from(
            new Set(response.map(item => `${item.exam_name} (${item.test_id})`))
        ).concat(''),
        data: response.map(x => ({...x, selected: true, add: '0'})),
    };
};

const getOrderSelect = async () => await apiCall('get', '/customer/order/select');

/* see https://tkdodo.eu/blog/react-query-data-transformations for passing select function into
   hook as an argument, specifically avoiding excess rerenders. we were getting stuck in a loop. */
export const useOrderSelectData = select => {
    note('useOrderSelectData called');
    const {data} = useQuery(['orders', 'select'], getOrderSelect, {select});
    return {orderDropDown: data};
};
